import React, {useState} from 'react';
import {Box, Collapse, IconButton, Stack, TableCell, TableRow, Tooltip, Typography} from "@mui/material"
import {KeyboardArrowDown, KeyboardArrowRight, WarningAmberOutlined} from "@mui/icons-material"
import {TestRunJson} from "../../api/json/TestRunJson"
import QualityCheckJson from "../../api/json/QualityCheckJson"
import {AnyTemplateJson} from "../../api/json/TemplateJson"
import {TemplateType} from "../../api/enum/TemplateType"
import {ScoringMethod} from "../../api/enum/ScoringMethod"
import TemplateUtil from "../../library/TemplateUtil"
import {TestRunRequests} from "../../api/requests/TestRunRequests"
import {useToasts} from "../../hooks/useToasts"
import ScoreInput from "./ScoreInput"
import MatchBadge from "./MatchBadge"
import TestRunActionMenu from "./TestRunActionMenu"
import LabeledValue from "../common/LabeledValue"
import ClassChip from "../common/ClassChip"
import {PredefinedOptionJson} from "../../api/json/PredefinedOptionJson"
import {normalizeToClass} from "../../library/ClassifierFilter"
import {resolveClassColor} from "../../library/ClassColors"

interface TestRunRowProps {
    qualityCheckJson: QualityCheckJson
    template: AnyTemplateJson
    testRun: TestRunJson
    classOptions?: PredefinedOptionJson[]
    didUpdate: (testRun: TestRunJson) => void
    didDelete: (exampleId: number) => void
}

const clampSx = {
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical" as const,
    overflow: "hidden",
}

function TestRunRow({qualityCheckJson, template, testRun, classOptions, didUpdate, didDelete}: TestRunRowProps) {
    const [expanded, setExpanded] = useState(false)
    const toasts = useToasts()

    const input = template.type === TemplateType.STANDARD
        ? TemplateUtil.renderAny(template.prompt, testRun.row.cells)
        : TemplateUtil.renderAny(template.user, testRun.row.cells)
    const expected = TemplateUtil.renderCompletionEquivalent(template, testRun.row.cells)
    const predicted = testRun.output ?? ""

    const classes = classOptions?.map(option => option.value) ?? []
    const predictedClass = normalizeToClass(testRun.output, classes)
    const expectedClass = normalizeToClass(expected, classes)

    const renderClassValue = (value: string) => {
        const matched = normalizeToClass(value, classes)
        return matched
            ? <ClassChip value={matched} color={resolveClassColor(matched, classOptions)}/>
            : <Box sx={clampSx}>{value}</Box>
    }

    const updateScore = (testRun: TestRunJson, newScore: number | null) => {
        TestRunRequests.updateScore(qualityCheckJson.id, testRun.row.id, newScore ? (newScore - 1) * 25 : 0)
            .then(didUpdate)
            .catch(toasts.showError)
    }

    return (
        <>
            <TableRow hover
                      onClick={() => setExpanded(value => !value)}
                      sx={{cursor: "pointer", "& > *": {borderBottom: expanded ? "unset" : undefined}}}>
                <TableCell padding="checkbox">
                    <IconButton size="small">
                        {expanded ? <KeyboardArrowDown fontSize="small"/> : <KeyboardArrowRight fontSize="small"/>}
                    </IconButton>
                </TableCell>
                <TableCell sx={{maxWidth: 0, color: "text.secondary"}}>
                    <Box sx={clampSx}>{input}</Box>
                </TableCell>
                <TableCell>
                    <Stack spacing={0.25} alignItems="flex-start">
                        {testRun.failed
                            ? <Tooltip title={testRun.failedReason ?? ""}>
                                <Box sx={{color: "error.main", fontWeight: 600}}>Failed</Box>
                            </Tooltip>
                            : renderClassValue(predicted)
                        }
                        {testRun.confidence != null && !testRun.failed &&
                            <Typography variant="caption" color="text.secondary">
                                {Math.round(testRun.confidence * 100)}% confidence
                            </Typography>
                        }
                    </Stack>
                </TableCell>
                <TableCell>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                        {renderClassValue(expected)}
                        {testRun.rowIssue &&
                            <Tooltip title="There may be an issue with this row.">
                                <WarningAmberOutlined color="warning" fontSize="small"/>
                            </Tooltip>
                        }
                    </Stack>
                </TableCell>
                <TableCell align="center" onClick={event => event.stopPropagation()}>
                    {classOptions && testRun.score != null
                        // Derived live from the current cells, so the badge agrees with the
                        // confusion matrix and filters after a ground-truth edit; the stored
                        // score stays frozen until the run is re-run.
                        ? <MatchBadge correct={predictedClass !== null && predictedClass === expectedClass}/>
                        : qualityCheckJson.scoringMethod === ScoringMethod.EXACT_MATCH && testRun.score != null
                            ? <MatchBadge correct={testRun.score === 100}/>
                            : <ScoreInput method={qualityCheckJson.scoringMethod} testRun={testRun} updateScore={updateScore}/>
                    }
                </TableCell>
                <TableCell align="right" padding="checkbox" onClick={event => event.stopPropagation()}>
                    <TestRunActionMenu qualityCheckJson={qualityCheckJson}
                                       testRun={testRun}
                                       didUpdate={didUpdate}
                                       didDelete={didDelete}
                                       iconButton/>
                </TableCell>
            </TableRow>

            <TableRow>
                <TableCell colSpan={6} sx={{py: 0, borderBottom: expanded ? undefined : "none"}}>
                    <Collapse in={expanded} unmountOnExit>
                        <Box sx={{py: 2, display: "grid", gap: 2}}>
                            <LabeledValue label="Input" value={input}/>
                            {testRun.reason &&
                                <LabeledValue label="Reasoning" value={testRun.reason}/>
                            }
                            {testRun.failedReason &&
                                <LabeledValue label="Error" value={testRun.failedReason}/>
                            }
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    )
}

export default TestRunRow;
